<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.vendorPriceModule.detail')"
        size="large"
        :submit="handleSubmit"
        @close="closeOverSide">
        <hts-card :title="$t('labels.vendor')">
            <div class="grid gap-x-2 grid-cols-5">
                <hts-form-input
                    v-model="dataEdit.vendorName"
                    :label="$t('labels.vendorPriceModule.name')"
                    class="col-span-2"
                    :creatable="true"
                    readonly/>
                <hts-form-input
                    v-model="dataEdit.contactName"
                    :label="$t('labels.vendorPriceModule.contact')"
                    :creatable="true"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.phone"
                    :label="$t('labels.phone')"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.email"
                    :label="$t('labels.email')"
                    readonly />
            </div>
        </hts-card>

        <hts-card :title="$t('labels.vendorPriceModule.priceInfo')">
            <div class="w-full">
                <hts-form-input
                    v-model="dataEdit.workflowName"
                    :label="$t('labels.workflow')"
                    readonly />
            </div>
            <div class="grid gap-x-2 grid-cols-2">
                <hts-form-input
                    v-model="dataEdit.effectiveFromDate"
                    :label="$t('labels.vendorPriceModule.effectiveFromDate')"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.effectiveToDate"
                    :label="$t('labels.vendorPriceModule.effectiveToDate')"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.oilPrice"
                    :label="$t('labels.vendorPriceModule.oilPrice')"
                    type="number"
                    readonly />
                <hts-form-input
                    v-model="dataEdit.oilPriceToTotalCost"
                    :label="$t('labels.vendorPriceModule.oilPriceToTotalCost')"
                    type="number"
                    readonly />
            </div>
        </hts-card>
        <hts-card :title="$t('labels.vendorPriceModule.shippingPrice')">
            <div
                v-for="(price, index) in dataEdit.prices"
                :key="price"
                class="grid gap-x-2 grid-cols-5">
                <hts-form-select
                    v-if="dataEdit.type === 'vehicle'"
                    v-model="price.listType"
                    :label="$t('labels.vendorPriceModule.typeVehicleOrCont')"
                    key-id="_id"
                    key-label="name"
                    class="col-span-2"
                    multiple
                    :selected-multiple="price.listTypeVehicleName"
                    name="vendorPriceModule.typeVehicleOrCont"
                    :prop="`typeVehicleOrCont${price.key}`"
                    readonly />
                <hts-form-select
                    v-else
                    v-model="price.listType"
                    :label="$t('labels.vendorPriceModule.typeVehicleOrCont')"
                    key-id="value"
                    :items="contTypes()"
                    key-label="label"
                    class="col-span-2"
                    multiple
                    name="vendorPriceModule.typeVehicleOrCont"
                    :prop="`typeVehicleOrCont${price.key}`"
                    readonly />
                <hts-form-input
                    v-model="price.price"
                    type="number"
                    :label="$t('labels.vendorPriceModule.price')"
                    name="vendorPriceModule.price"
                    :prop="`price${price.key}`"
                    readonly />
                <hts-form-input
                    v-model="price.output"
                    type="number"
                    :label="$t('labels.vendorPriceModule.output')"
                    name="vendorPriceModule.output"
                    :prop="`output${price.key}`"
                    readonly />

                <hts-form-button
                    v-if="price.allowances && price.allowances.length"
                    class="mt-12 w-full"
                    color="info"
                    icon-position="right"
                    type="button"
                    size="mini"
                    :outline="true"
                    @click="showModalAddAllowancePrices(index)">
                    {{ $t('labels.vendorPriceModule.viewAllowancePrice') }}
                </hts-form-button>
            </div>

            <hts-form-textarea
                v-model="dataEdit.description"
                :label="$t('labels.description')"
                :color="props.color"
                readonly />
        </hts-card>

        <hts-modal v-model="modalAddPrices">
            <template #custom>
                <hts-card
                    :title="$t('labels.vendorPriceModule.allowancePrice')"
                    class="mt-5">
                    <template v-if="allowancePrices">
                        <div
                            v-for="(allowancePrice, index) in allowancePrices"
                            :key="allowancePrice"
                            class="grid gap-x-2 grid-cols-2">
                            <hts-form-select
                                v-model="allowancePrice.goodsType"
                                :label="$t('labels.vendorPriceModule.typeCommodity')"
                                :items="goodsTypes()"
                                readonly />
                            <hts-form-input
                                v-model="allowancePrice.price"
                                type="number"
                                :label="$t('labels.vendorPriceModule.price')"
                                readonly />
                        </div>
                    </template>

                    <div class="flex justify-end flex-row gap-2 mt-2">
                        <hts-form-button
                            icon-name="hi-x-mark-icon"
                            color="error"
                            @click="modalAddPrices = false">
                            {{ $t('labels.cancel') }}
                        </hts-form-button>
                    </div>
                </hts-card>
            </template>
        </hts-modal>
        <template #footer>
            <div class="flex flex-row gap-5">
                <hts-form-button
                    icon-name="hi-x-mark-icon"
                    icon-position="right"
                    color="error"
                    @click="closeOverSide">
                    {{ $t('labels.cancel') }}
                </hts-form-button>
            </div>
        </template>
    </hts-drawer>
</template>

<script lang="ts" setup>
import { dataSelectWorkflow, contTypes, IVendorPrice, initAdd, getWorkflowName } from './ignore/data'
import { ruleAddVendorPrices } from './ignore/form'
import { $api, $lodash } from '#imports'
const props = defineProps({
  color: {
    type: String,
    default: 'theme'
  },
  sizeSlideOver: {
    type: String,
    default: 'small'
  },
  column: {
    type: Number,
    default: 2
  }
})

const emits = defineEmits(['refreshTables'])

const visible = ref(false)

const open = (id: string) => {
  if (id) {
    const api = $url.price.vendor.get

    $api(api, { uriParams: { ID: id || '' } }).then((data) => {
      const resData: any = data?.data?.value

      if (resData.success) {
        const vendorPrice: IVendorPrice = resData.data

        if (vendorPrice.vendor && typeof vendorPrice.vendor !== 'string') {
          vendorPrice.vendorName = vendorPrice.vendor.name
        }

        if (vendorPrice.contact && typeof vendorPrice.contact !== 'string') {
          vendorPrice.email = vendorPrice.contact?.email
          vendorPrice.phone = vendorPrice.contact?.phone
          vendorPrice.contactName = vendorPrice.contact?.fullName
        }

        if (vendorPrice.workflow && Array.isArray(dataSelectWorkflow)) {
          vendorPrice.workflowName = getWorkflowName(vendorPrice.workflow)
          dataSelectWorkflow.forEach((workflow) => {
            if (workflow._id === vendorPrice.workflow) {
              vendorPrice.type = selectWorkflow(workflow)
            }
          })
        }

        vendorPrice?.prices?.forEach((price, index) => {
          price.key = index
        })

        dataEdit.value = vendorPrice
        visible.value = true
      }
    })
  }
}

const closeOverSide = () => {
  visible.value = false
}

const init: IVendorPrice = {
  vendor: null,
  contact: null,
  email: null,
  phone: null,
  workflow: null,
  type: null,
  effectiveFromDate: null,
  effectiveToDate: null,
  oilPrice: null,
  oilPriceToTotalCost: null,
  prices: [
    {
      listType: null,
      price: null,
      output: null,
      allowances: [],
      key: 1
    }
  ],
  description: null
}

const dataEdit = ref<IVendorPrice>({ ...init })
const popover = ref()
const modalAddPrices = ref(false)
const allowancePrices = ref<null | Array<{ goodsType: string | null | undefined; price: number | null | undefined }>>(null)
const indexPrice = ref<number | undefined>(undefined)
defineExpose({ open })

const columnClass = computed(() => {
  if ($lodash.inRange(props.column, 1, 12)) {
    return `grid-cols-${props.column}`
  }

  return 'grid-cols-1'
})

const selectWorkflow = (workflow: any) => {
  if (Array.isArray(workflow) && workflow.length) {
    if (workflow[0].isCont === true) {
      dataEdit.value.type = 'cont'
      return 'cont'
    }
    if (workflow[0].isCont === false) {
      dataEdit.value.type = 'vehicle'
      return 'vehicle'
    }
  }

  if (workflow?.isCont === true) {
    dataEdit.value.type = 'cont'
    return 'cont'
  }
  if (workflow?.isCont === false) {
    dataEdit.value.type = 'vehicle'
    return 'vehicle'
  }
  dataEdit.value.type = ''
  return ''
}

const handleSubmit = $lodash.debounce(() => {}, 300)

const showModalAddAllowancePrices = (index: number) => {
  if (
    !dataEdit.value.prices[index]?.allowances ||
    !Array.isArray(dataEdit.value.prices[index].allowances) ||
    !dataEdit.value.prices[index].allowances?.length
  ) {
    allowancePrices.value = [
      {
        goodsType: null,
        price: null
      }
    ]
  } else {
    let item: any = []

    item = dataEdit.value.prices[index]?.allowances?.map((itemAllowances) => {
      if (itemAllowances) {
        return { ...itemAllowances }
      }
      return {
        goodsType: null,
        price: null
      }
    })

    allowancePrices.value = item
  }
  indexPrice.value = index
  modalAddPrices.value = true
}
</script>
